import { post } from '@/api/client';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const osloDay = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(new Date(iso));

/**
 * An unfinished setup follows the administrator around (owner, 2026-09-27): they may leave it to add
 * people or look around, and this is the way back, on every screen but the setup itself.
 */
export function SetupBanner() {
  const { tenant } = useTenant();
  const nav = useNavigate();
  const { pathname } = useLocation();
  if (!tenant?.setup_step || !tenant.me?.is_admin || tenant.is_demo || pathname === '/oppsett') return null;
  return (
    <output className="banner" data-testid="setup-banner">
      <span>{T('Oppsettet av {company} er ikke ferdig.', { company: tenant.name })}</span>
      <Button small tone="pri" onClick={() => nav('/oppsett')}>
        {T('Fortsett oppsettet')}
      </Button>
    </output>
  );
}

/**
 * The free trial and what follows it (plan 0028 slices 3 and 4, owner 2026-09-27): until when it is
 * free, to the administrator; after it, a week of reading with a banner to everyone, then locked. The
 * administrator gets the way to Abonnement. A company that pays says nothing unless a payment failed.
 */
export function TrialBanner() {
  const { tenant } = useTenant();
  const nav = useNavigate();
  const { pathname } = useLocation();
  if (!tenant || tenant.is_demo || pathname === '/abonnement') return null;
  const admin = !!tenant.me?.is_admin;
  const access = tenant.billing?.access ?? 'full';
  const paying = ['trialing', 'active'].includes(tenant.subscription_status ?? '');
  const toBilling = admin && tenant.billing?.enabled && (
    <Button small tone="pri" onClick={() => nav('/abonnement')}>
      {T('Start abonnementet')}
    </Button>
  );
  if (tenant.subscription_status === 'past_due' && admin)
    return (
      <output className="banner" data-testid="trial-banner">
        <span>{T('Siste betaling feilet. Oppdater kortet for å beholde tilgangen.')}</span>
        <Button small tone="pri" onClick={() => nav('/abonnement')}>
          {T('Oppdater betaling')}
        </Button>
      </output>
    );
  if (access !== 'full')
    return (
      <output className="banner" data-testid="trial-banner">
        <span>
          {access === 'read'
            ? T(
                'Prøveperioden er over. Du kan se og eksportere, men ikke endre noe før abonnementet er startet.',
              )
            : T('Prøveperioden er over og bedriften er låst.')}
        </span>
        {toBilling}
      </output>
    );
  if (paying || !admin || !tenant.trial_ends_at || new Date(tenant.trial_ends_at) < new Date()) return null;
  // the plain trial line lives in the white top bar on a wide screen (TrialChip, owner 2026-10-22);
  // this banner stays for narrow ones, where the top bar has no room
  return (
    <output className="banner trial-narrow" data-testid="trial-banner">
      <span>
        {T('Gratis prøveperiode til {date}. Du blir ikke belastet.', {
          date: fmtD(osloDay(tenant.trial_ends_at)),
        })}
      </span>
      {toBilling}
    </output>
  );
}

/**
 * The free trial in the top bar (owner 2026-10-22: out of the page, where it took a row above every
 * screen): the same sentence and button as TrialBanner's plain case, and only that case; a failed
 * payment or an ended trial stays a banner in the page.
 */
export function TrialChip() {
  const { tenant } = useTenant();
  const nav = useNavigate();
  const { pathname } = useLocation();
  if (!tenant || tenant.is_demo || pathname === '/abonnement' || !tenant.me?.is_admin) return null;
  if ((tenant.billing?.access ?? 'full') !== 'full' || tenant.subscription_status === 'past_due') return null;
  if (['trialing', 'active'].includes(tenant.subscription_status ?? '')) return null;
  if (!tenant.trial_ends_at || new Date(tenant.trial_ends_at) < new Date()) return null;
  return (
    <span className="ttrial" data-testid="trial-chip">
      <span>
        {T('Gratis prøveperiode til {date}. Du blir ikke belastet.', {
          date: fmtD(osloDay(tenant.trial_ends_at)),
        })}
      </span>
      {tenant.billing?.enabled && (
        <Button small tone="pri" onClick={() => nav('/abonnement')}>
          {T('Start abonnementet')}
        </Button>
      )}
    </span>
  );
}

/**
 * A private demo says what it is on every screen (plan 0028 slice 2): invented people, nobody gets
 * mail or notifications from it, when it is deleted, and the two ways on: set up a real company
 * (a separate one; nothing is copied), or put the demo back to its fresh week.
 */
export function DemoBanner() {
  const { tenant, workspace } = useTenant();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!tenant?.is_demo) return null;

  async function reset() {
    setBusy(true);
    try {
      await post('/demo/reset', {});
      await qc.invalidateQueries();
      toast(T('Demodataene er tilbakestilt.'));
    } catch (e) {
      toast(`${T('Kunne ikke tilbakestille')}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <output className="banner demo" data-testid="demo-banner">
      <span>
        <b>{T('Demobedrift')}</b> {T('Personene er oppdiktet, og ingen får e-post eller varsler herfra.')}
        {tenant.demo_expires_at
          ? ` ${T('Slettes {date}.', { date: fmtD(osloDay(tenant.demo_expires_at)) })}`
          : ''}
      </span>
      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {confirming ? (
          <>
            <span>
              {T(
                'Vakter, fravær og timebank settes tilbake. Personer og typer du har lagt til, blir værende.',
              )}
            </span>
            <Button small ghost onClick={() => setConfirming(false)} disabled={busy}>
              {T('Avbryt')}
            </Button>
            <Button small onClick={reset} disabled={busy}>
              {busy ? '…' : T('Ja, tilbakestill')}
            </Button>
          </>
        ) : (
          <Button small ghost onClick={() => setConfirming(true)}>
            {T('Tilbakestill demodata')}
          </Button>
        )}
        {/* from a demo without planning (the trade firm) the own company starts the same way (#118) */}
        <Button
          small
          tone="pri"
          onClick={() =>
            nav(workspace.enabled.scheduling ? '/konto/opprett-bedrift' : '/konto/opprett-bedrift?demo=bygg')
          }
        >
          {T('Sett opp din bedrift')}
        </Button>
      </span>
    </output>
  );
}
